import type { DataPoints } from '@retracedgmbh/data-points-lib';
import DataPointsLib from '@retracedgmbh/data-points-lib';
import {
  actorFromAuditMeta,
  ENTITY_HISTORY_ENTITY_TYPES,
  ENTITY_HISTORY_EVENT_TYPES,
  ENTITY_HISTORY_SOURCES,
  EntityHistoryService,
  type EntityHistorySource,
} from '../../../../entity-history/index.js';
import type { DbClient } from '../../../../lib/db.js';
import {
  COMPANY_CORE_INFO_TEMPLATES,
  RELATIONAL_DATA_POINTS_TO_COPY,
} from '../../../constants.js';
import { insertCompanyProfileIfAbsentDb } from '../../db/company-profiles/insert-if-absent.js';
import { insertMissingCoreProfilesDb } from '../../db/company-profiles/insert-missing-core-profiles.js';
import { listCompanyProfileTypesByIdsDb } from '../../db/company-profiles/list-types-by-ids.js';
import { getDataPointAnswersByDataPointsDb } from '../../db/data-point-answers/get-by-data-points.js';
import type { DataPointAnswerRow } from '../../db/data-point-answers/types.js';
import { getTemplatesByIdsDb } from '../../db/templates/get-by-ids.js';
import type { Template } from '../../db/templates/types.js';
import type { CompanyIds } from '../types.js';

type DataPointValue = { value: unknown };
type TemplateAnswer = Record<string, DataPointValue>;
type AuditData = Record<string, unknown>;

const { ABOUT, OTHER_IDS, OPERATIONAL_METRICS } = COMPANY_CORE_INFO_TEMPLATES;

/** The core templates this service fills in next to an existing `ABOUT`. */
const COMPLEMENT_TEMPLATE_IDS: string[] = [OTHER_IDS, OPERATIONAL_METRICS];

const AUDIT_COLUMNS = [
  'createdByImpersonateId',
  'createdByUserId',
  'createdByCompanyId',
  'createdByApiKeyId',
  'createdByApiKeyLabel',
  'updatedByImpersonateId',
  'updatedByImpersonateName',
  'updatedByUserId',
  'updatedByUserName',
  'updatedByCompanyId',
  'updatedByCompanyName',
  'updatedByApiKeyId',
  'updatedByApiKeyLabel',
] as const;

const asString = (value: unknown): string | null =>
  typeof value === 'string' && value !== '' ? value : null;

const parseAnswer = (row: DataPointAnswerRow): DataPointValue | null => {
  if (!row.answer) return null;
  const parsed = (
    typeof row.answer === 'string' ? JSON.parse(row.answer) : row.answer
  ) as DataPointValue | null;
  return parsed?.value == null ? null : parsed;
};

export type CompanyCoreProfilesServiceDeps = {
  db: DbClient;
};

type HistoryContext = {
  auditData: AuditData;
  /** Where the write came from, for the profile history; defaults to SYSTEM. */
  source?: EntityHistorySource;
  correlationId?: string | null;
};

export type EnsureCoreProfilesParams = HistoryContext & {
  companyIds: CompanyIds;
};

export type BackfillCoreProfilesParams = HistoryContext;

/**
 * Keeps a relationship's core profile set (`ABOUT`, `OTHER_IDS`,
 * `OPERATIONAL_METRICS`) whole.
 *
 * Linking and company creation always write all three, but paths that write a
 * single template — the GLOBAL data-point sync fanning out over
 * `companies_service_company_links`, and historically a one-off regeneration
 * migration — created an `ABOUT` row for relationships that had none, and
 * nothing else. Readers that join the other two (risk analysis's assessed
 * companies and assessment count, the full-profile reads) then silently
 * dropped the partner.
 *
 * `ABOUT` is what makes a relationship a network view, so it is the trigger and
 * never the output: a triple with an `ABOUT` gets whichever of `OTHER_IDS` /
 * `OPERATIONAL_METRICS` it lacks, and a triple without one is left alone (a
 * stray `WEBSHOP_PROFILE` row, say, must not become a network connection).
 *
 * Missing templates are never written over an existing row. Each is built the
 * way `CompanyLinkService` builds a link's profile: whatever this relationship
 * already answered, then the profiled company's own shareable (GLOBAL +
 * copyable RELATIONAL) answers, `{value: null}` for the rest.
 */
export class CompanyCoreProfilesService {
  private readonly dataPoints: DataPoints;
  private readonly history: EntityHistoryService;
  private coreTemplates: Template[] | undefined;

  constructor(private readonly deps: CompanyCoreProfilesServiceDeps) {
    this.dataPoints = new DataPointsLib(console).initializeDataPoints();
    this.history = new EntityHistoryService({ db: deps.db });
  }

  /** Creates the core profiles `companyIds` lacks next to its `ABOUT`; returns the templates it created. */
  async ensureCoreProfiles(
    params: EnsureCoreProfilesParams,
  ): Promise<string[]> {
    const { companyIds, auditData } = params;

    const existing = await listCompanyProfileTypesByIdsDb(this.deps.db, {
      ...companyIds,
      profileTypes: [ABOUT, ...COMPLEMENT_TEMPLATE_IDS],
    });
    if (!existing.includes(ABOUT)) return [];
    const missing = COMPLEMENT_TEMPLATE_IDS.filter(
      id => !existing.includes(id),
    );
    if (missing.length === 0) return [];

    const templates = await this.getCoreTemplates();
    const audit = this.buildAuditColumns(auditData);

    const created: string[] = [];
    for (const templateId of missing) {
      const template = templates.find(t => t.id === templateId);
      if (!template) continue;

      const data = await this.buildProfileData(companyIds, template);
      const inserted = await insertCompanyProfileIfAbsentDb(this.deps.db, {
        ...companyIds,
        profileType: templateId,
        data,
        ...audit,
      });
      if (!inserted) continue;

      created.push(templateId);
      await this.history.record(
        this.createdEntry(companyIds, templateId, params),
      );
    }

    return created;
  }

  /**
   * `ensureCoreProfiles` for every relationship at once, for a data backfill:
   * one set-based statement per template (`insertMissingCoreProfilesDb`) plus
   * batched history, instead of ~10 round trips per relationship. Returns how
   * many profiles it created per template.
   */
  async backfillMissingCoreProfiles(
    params: BackfillCoreProfilesParams,
  ): Promise<Record<string, number>> {
    const templates = await this.getCoreTemplates();
    const audit = this.buildAuditColumns(params.auditData);

    const createdByTemplate: Record<string, number> = {};
    for (const template of templates) {
      const created = await insertMissingCoreProfilesDb(this.deps.db, {
        profileType: template.id,
        composedOf: template.composedOf,
        shareableDataPointIds: template.composedOf.filter(id =>
          this.isShareable(id),
        ),
        setRetracedId: template.id === OTHER_IDS,
        audit: {
          createdAt: audit.createdAt as string,
          updatedAt: audit.updatedAt as string,
          createdByUserId: audit.createdByUserId as string | null,
          updatedByUserId: audit.updatedByUserId as string | null,
          updatedByUserName: audit.updatedByUserName as string | null,
        },
      });

      createdByTemplate[template.id] = created.length;
      await this.history.recordMany(
        created.map(companyIds =>
          this.createdEntry(companyIds, template.id, params),
        ),
      );
    }

    return createdByTemplate;
  }

  /** The `CREATED` entry that opens a filled-in profile's history. */
  private createdEntry(
    companyIds: CompanyIds,
    templateId: string,
    context: HistoryContext,
  ) {
    return {
      entityType: ENTITY_HISTORY_ENTITY_TYPES.COMPANY_PROFILE,
      entityId: companyIds.forCompanyId,
      entitySubType: templateId,
      eventType: ENTITY_HISTORY_EVENT_TYPES.CREATED,
      source: context.source ?? ENTITY_HISTORY_SOURCES.SYSTEM,
      correlationId: context.correlationId,
      companyIds,
      changes: null,
      metadata: {
        entityType: ENTITY_HISTORY_ENTITY_TYPES.COMPANY_PROFILE,
        templateId,
      },
      createdBy: actorFromAuditMeta(context.auditData),
    };
  }

  /** Template definitions never change at runtime — fetched once per instance. */
  private async getCoreTemplates(): Promise<Template[]> {
    this.coreTemplates ??= await getTemplatesByIdsDb(
      this.deps.db,
      COMPLEMENT_TEMPLATE_IDS,
    );
    return this.coreTemplates;
  }

  private buildAuditColumns(auditData: AuditData): Record<string, unknown> {
    const now = new Date().toISOString();
    const columns: Record<string, unknown> = {
      createdAt: asString(auditData.createdAt) ?? now,
      updatedAt: asString(auditData.updatedAt) ?? now,
    };
    for (const key of AUDIT_COLUMNS) {
      columns[key] = asString(auditData[key]);
    }
    return columns;
  }

  private async buildProfileData(
    companyIds: CompanyIds,
    template: Template,
  ): Promise<TemplateAnswer> {
    const { forCompanyId, byCompanyId, onBehalfOfCompanyId } = companyIds;
    const isSelf =
      forCompanyId === byCompanyId && byCompanyId === onBehalfOfCompanyId;

    const answer: TemplateAnswer = {};
    for (const dataPointId of template.composedOf) {
      answer[dataPointId] = { value: null };
    }

    const relationshipAnswers = await getDataPointAnswersByDataPointsDb(
      this.deps.db,
      { ...companyIds, retracedDataPointIds: template.composedOf },
    );
    this.overlay(answer, relationshipAnswers);

    if (!isSelf) {
      const ownAnswers = await getDataPointAnswersByDataPointsDb(this.deps.db, {
        forCompanyId,
        byCompanyId: forCompanyId,
        onBehalfOfCompanyId: forCompanyId,
        retracedDataPointIds: template.composedOf,
      });
      this.overlay(
        answer,
        ownAnswers.filter(row => this.isShareable(row.retracedDataPointId)),
      );
    }

    // SYSTEM-typed, so never among the copied answers — set directly, as
    // linking and company creation do.
    if (template.id === OTHER_IDS) {
      answer.retraced_id = { value: forCompanyId };
    }

    return answer;
  }

  /** Fills still-empty keys of `answer` from `rows`; never replaces a value already set. */
  private overlay(answer: TemplateAnswer, rows: DataPointAnswerRow[]): void {
    for (const row of rows) {
      if (answer[row.retracedDataPointId]?.value != null) continue;
      const parsed = parseAnswer(row);
      if (parsed) answer[row.retracedDataPointId] = parsed;
    }
  }

  /** What linking copies from a company's own profile onto a partner's view of it. */
  private isShareable(dataPointId: string): boolean {
    let type: string | undefined;
    try {
      type =
        this.dataPoints.getDataPointById(dataPointId)?.dataPointDefinition
          ?.type;
    } catch {
      return false;
    }
    return (
      type === 'GLOBAL' ||
      (type === 'RELATIONAL' &&
        RELATIONAL_DATA_POINTS_TO_COPY.includes(dataPointId))
    );
  }
}
